import { useState } from "react";
import { InfoCircleOutlined } from '@ant-design/icons';
import { Space, Button, message, Col, Input, Row, Select, Tooltip, Typography, Popconfirm } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import Table, { ColumnsType } from "antd/lib/table";
import { convertDateTime, fNumberToLocalString, getIdFromObjectId } from "utils";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { deleteRateLimitLogs, getRateLimitLogs, truncateRateLimitLogs } from "services/rate-limit-log";
import { deleteRequestLogs, getRequestLogs, truncateRequestLogs } from "../../services/request-logs";

interface DataType {
  _id: string;
  requestUrl: string;
  email: string;
  count?: number;
  createdAt: string | null;
}

interface StateType {
  email: string | undefined,
  filterMonth: number | null,
  limit: number,
  skip: number
}

const monthOptions = [
  { label: '1', value: 1, key: 1 },
  { label: '2', value: 2, key: 2 },
  { label: '3', value: 3, key: 3 },
]

const initialState = {
  email: undefined,
  filterMonth: null,
  limit: 5000,
  skip: 0
}

const Logs: React.FC = () => {
  const [dataTable, setDataTable] = useState<DataType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [state, setState] = useState<StateType>(initialState)
  const [total, setTotal] = useState<number>(0)
  const [selectedLogs, setSelectedLogs] = useState<React.Key[]>([])

  let { pathname } = useLocation();

  const pageTitle: string = pathname === "/logs/rate-limit-logs" ? "Rate Limit Logs" : "Request Logs"

  const handleOnSearch = (search: StateType) => {
    setLoading(true)
    const params = {
      email: !search.email ? 'all' : search.email,
      filterMonth: search.filterMonth,
      limit: initialState.limit,
      skip: initialState.skip
    }
    const getLogs: any = pathname === "/logs/rate-limit-logs" ? getRateLimitLogs : getRequestLogs

    return getLogs(params)
      .then(({ data }: any) => {
        // Response structure: { status: 200, data: { result: [], total: ..., current_total: ... } }
        const responseData = data?.data || data;
        const result = responseData?.result || [];
        const total = responseData?.total || 0;
        const currentTotal = responseData?.current_total || 0;
        
        setState((prev: StateType) => {
          setTotal(total)
          return ({
            ...prev,
            skip: currentTotal,
          })
        })
        setDataTable(() => result.map((d: DataType) => ({
          ...d,
          _id: getIdFromObjectId(d._id),
          key: getIdFromObjectId(d._id)
        })))
      })
      .catch(() => message.error(`Cannot get logs of ${state.email}`))
      .finally(() =>
        setLoading(false)
      )
  }

  const handleOnLoadMore = () => {
    setLoading(true)
    const params = {
      ...state,
      email: !state.email ? 'all' : state.email,
      skip: state.skip
    }
    const getLogs: any = pathname === "/logs/rate-limit-logs" ? getRateLimitLogs : getRequestLogs

    return getLogs(params)
      .then(({ data }: any) => {
        // Response structure: { status: 200, data: { result: [], total: ..., current_total: ... } }
        const responseData = data?.data || data;
        const result = responseData?.result || [];
        const total = responseData?.total || 0;
        const currentTotal = responseData?.current_total || 0;
        
        setState((prev: StateType) => {
          setTotal(total)
          return ({
            ...prev,
            skip: currentTotal,
          })
        })
        setDataTable((prev: DataType[]) => {
          const newData = result.map((d: DataType) => ({
            ...d,
            _id: getIdFromObjectId(d._id),
            key: getIdFromObjectId(d._id)
          }))
          return [...prev, ...newData]
        })
      })
      .catch(() => message.error(`Cannot get logs of ${state.email}`))
      .finally(() =>
        setLoading(false)
      )
  }

  const handleOnTruncateLogs = (ids: React.Key[]) => {
    setLoading(true)
    const params = {
      log_ids: ids
    }
    const truncateLogs: any = pathname === "/logs/rate-limit-logs" ? truncateRateLimitLogs : truncateRequestLogs

    return truncateLogs(params)
      .then(() => {
        setDataTable((prev: DataType[]) =>
          ids.length === prev.length ? [] : prev.filter((d) => !ids.includes(d._id)))
        message.success('Deleted seleted logs successfully')
      })
      .catch(() => message.error('Cannot delete selected logs'))
      .finally(() => setLoading(false))
  }

  const handleOnDeleteLogs = () => {
    if (!state.email) {
      return
    }

    const params = {
      email: state.email,
      filterMonth: state.filterMonth
    }

    const deleteLogs: any = pathname === "/logs/rate-limit-logs" ? deleteRateLimitLogs : deleteRequestLogs

    return deleteLogs(params)
      .then(() => {
        setDataTable([])
        message.success("Deleted user's logs successfully")
      })
      .catch(() => message.error("Cannot delete user's logs"))
      .finally(() => setLoading(false))
  }

  const columns: ColumnsType<DataType> = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
    },
    {
      title: 'Request URL',
      dataIndex: 'requestUrl',
      key: 'regionShortName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => convertDateTime(value) // API returns ISO string, convertDateTime handles it
    },
  ];

  const additionalColumn: ColumnsType<DataType> = [{
    title: 'Count',
    dataIndex: 'count',
    key: 'count',
    width: 100
  }]

  return (
  <>
    <Helmet>
      <title>{pageTitle} | Admin page</title>
    </Helmet>
    <PageHeader
      className="page-header"
      backIcon={true}
      title={pageTitle}
      footer={
        <>
          <Input.Group style={{ marginBottom: 15 }}>
            <Row gutter={8}>
              <Col span={6}>
                <Input
                  value={state.email}
                  onChange={(e) => setState((prev: any) => ({ ...prev, email: e.target.value }))}
                  placeholder="Search for email"
                  allowClear
                />
              </Col>
              <Col span={3}>
                <Select
                  loading={loading}
                  value={state.filterMonth}
                  options={monthOptions}
                  onSelect={(value: number) => setState((prev: any) => ({ ...prev, filterMonth: value }))}
                  style={{ width: 150, marginRight: 5 }}
                  placeholder="Months"
                />
                <Tooltip title="Select number of months from now to get logs">
                  <InfoCircleOutlined />
                </Tooltip>
              </Col>
              <Col span={6}>
                <Button type="primary" disabled={!state.filterMonth} onClick={() => handleOnSearch(state)}>Search</Button>
              </Col>
            </Row>
          </Input.Group>
          <Table
            columns={pathname === "/logs/rate-limit-logs" ? columns : [...columns, ...additionalColumn]}
            dataSource={dataTable}
            loading={loading}
            pagination={false}
            scroll={{ x: true, y: 500 }}
            rowSelection={{
              onChange: (selectedRowKeys: React.Key[]) => setSelectedLogs(selectedRowKeys),
            }}
            title={() => 
              <Space style={{ display: 'flex', justifyContent: 'space-between'  }}>
                <Typography><strong>Total:</strong> {state.skip}</Typography>
                <Space>
                  <Popconfirm
                    placement="bottomLeft"
                    title="Are you sure to delete seleted log(s)?"
                    onConfirm={() => handleOnTruncateLogs(selectedLogs)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button danger disabled={selectedLogs.length === 0}>Delete Selected Row(s)</Button>
                  </Popconfirm>
                  <Popconfirm
                    placement="bottomLeft"
                    title="Are you sure to delete user's all of logs?"
                    onConfirm={() => handleOnDeleteLogs()}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button danger type="primary" disabled={dataTable.length === 0}>Delete all of logs</Button>
                  </Popconfirm>
                </Space>
              </Space>
            }
            footer={()=> 
              (total > state.skip) && <Typography>
                Too much logs {`(${fNumberToLocalString(total)})`},
                but only showing {`(${fNumberToLocalString(state.skip)})`} of logs.
                Click <Button type="link" onClick={() => handleOnLoadMore()} style={{ width: 35, padding: 0, margin: 1 }}>here</Button>
                to load {`(${fNumberToLocalString(state.limit)})`} more logs
              </Typography>
            }
          />
        </>
      }
    />
  </>
  )
}

export default Logs;